import axios from "axios";

const REGION = "us-east-2";
const BUCKET = "airtableuploads";
export type FileResponseType = "arraybuffer" | "blob" | "json" | "text";

// URLs known to 404 in this session. Many Airtable attachments don't exist under their
// recordId folder, so the first probe always 404s and DevTools logs it. We cache the misses
// so repeated downloads (effect re-runs, multiple consumers of the same job) skip the dead
// path. Only 404s are cached — transient errors (CORS, offline, 5xx) still retry.
const known404 = new Set<string>();

export namespace AWS {
	export namespace S3 {
		export async function download<T = ArrayBuffer | Blob | string>(
			filename: string,
			responseType: FileResponseType,
			recordId = "",
			bucket: string = BUCKET,
		) {
			const primaryUrl = _makeS3Url(filename, recordId, bucket);
			const rootUrl = _makeS3Url(filename, "", bucket);

			if (!known404.has(primaryUrl)) {
				try {
					const response = await axios.get<T>(primaryUrl, { responseType });
					if (response.status === 200) return response.data;
				} catch (err) {
					if (axios.isAxiosError(err) && err.response?.status === 404) {
						known404.add(primaryUrl);
					}
					// fall through to root attempt
				}
			}

			if (primaryUrl === rootUrl || known404.has(rootUrl)) return undefined;
			try {
				const response2 = await axios.get<T>(rootUrl, { responseType });
				if (response2.status === 200) return response2.data;
			} catch (err) {
				if (axios.isAxiosError(err) && err.response?.status === 404) {
					known404.add(rootUrl);
				}
			}
			return undefined;
		}

		function _makeS3Url(filename: string, folder = "", bucket: string = BUCKET) {
			folder = folder == "" ? "" : folder.endsWith("/") ? folder : `${folder}/`;
			filename = _isEncoded(filename) ? filename : encodeURIComponent(filename);
			return `https://${bucket}.s3.${REGION}.amazonaws.com/${folder}${filename}`;
		}

		function _isEncoded(uri: string) {
			uri = uri || "";

			return uri !== decodeURIComponent(uri);
		}
	}
}
